import { supabase } from '../lib/supabase'
import { supabaseAdmin } from '../lib/supabaseAdmin'

export interface Notification {
  id: string
  userId: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  timestamp: Date
  read: boolean
  actionUrl?: string
  metadata?: Record<string, any>
}

export interface CreateNotificationData {
  userId: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  actionUrl?: string
  metadata?: Record<string, any>
  useAdminClient?: boolean // Flag to use admin client for system notifications
}

class NotificationService {
  // Create a new notification
  async createNotification(data: CreateNotificationData): Promise<{ success: boolean; notification?: Notification; error?: string }> {
    try {
      // Use admin client for system notifications, regular client for user notifications
      const client = data.useAdminClient ? supabaseAdmin : supabase
      
      if (!client) {
        throw new Error(data.useAdminClient ? 'Supabase admin client not configured' : 'Supabase not configured')
      }

      const notificationData = {
        user_id: data.userId,
        type: data.type,
        title: data.title,
        message: data.message,
        action_url: data.actionUrl,
        metadata: data.metadata || {},
        is_read: false,
        created_at: new Date().toISOString()
      }

      console.log(`Creating notification for user ${data.userId} using ${data.useAdminClient ? 'admin' : 'regular'} client`)

      const { data: notification, error } = await client
        .from('notifications')
        .insert(notificationData)
        .select()
        .single()

      if (error) {
        console.error('Database error creating notification:', error)
        throw error
      }

      return { 
        success: true, 
        notification: {
          id: notification.id,
          userId: notification.user_id,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          timestamp: new Date(notification.created_at),
          read: notification.is_read,
          actionUrl: notification.action_url,
          metadata: notification.metadata
        }
      }
    } catch (error) {
      console.error('Error creating notification:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Get notifications for a user
  async getNotifications(userId: string, limit: number = 50): Promise<{ success: boolean; notifications?: Notification[]; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      console.log('🔍 Fetching notifications for user:', userId)

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error

      console.log('📥 Raw notification data:', data)

      const notifications: Notification[] = (data || []).map(n => ({
        id: n.id,
        userId: n.user_id,
        type: n.type,
        title: n.title,
        message: n.message,
        timestamp: new Date(n.created_at),
        read: n.is_read,
        actionUrl: n.action_url,
        metadata: n.metadata || {}
      }))

      console.log('✅ Processed notifications:', notifications.length, notifications)

      return { success: true, notifications }
    } catch (error) {
      console.error('❌ Error fetching notifications:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error marking notification as read:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Mark all notifications as read for a user
  async markAllAsRead(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Delete notification
  async deleteNotification(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      console.log('🗑️ Deleting notification:', notificationId)

      const { data, error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .select()

      if (error) {
        console.error('❌ Database error deleting notification:', error)
        throw error
      }

      console.log('✅ Notification deleted successfully:', data)
      return { success: true }
    } catch (error) {
      console.error('❌ Error deleting notification:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Get unread count for a user
  async getUnreadCount(userId: string): Promise<{ success: boolean; count?: number; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false)

      if (error) throw error

      return { success: true, count: count || 0 }
    } catch (error) {
      console.error('Error getting unread count:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Create plan upgrade notification
  async createPlanUpgradeNotification(userId: string, planName: string): Promise<{ success: boolean; error?: string }> {
    const notificationData: CreateNotificationData = {
      userId,
      type: 'success',
      title: '🎉 Plan Upgraded Successfully!',
      message: `Welcome to ${planName}! Your account has been upgraded and all premium features are now active. Enjoy unlimited tasks, AI automation, and advanced analytics.`,
      actionUrl: '/upgrade',
      metadata: {
        planName,
        upgradeDate: new Date().toISOString(),
        notificationType: 'plan_upgrade'
      }
    }

    return await this.createNotification(notificationData)
  }

  // Create plan downgrade notification
  async createPlanDowngradeNotification(userId: string, planName: string): Promise<{ success: boolean; error?: string }> {
    const notificationData: CreateNotificationData = {
      userId,
      type: 'info',
      title: 'Plan Changed',
      message: `Your plan has been changed to ${planName}. Some features may no longer be available, but your existing data is safe.`,
      actionUrl: '/upgrade',
      metadata: {
        planName,
        changeDate: new Date().toISOString(),
        notificationType: 'plan_downgrade'
      }
    }

    return await this.createNotification(notificationData)
  }

  // Create payment failure notification
  async createPaymentFailureNotification(userId: string): Promise<{ success: boolean; error?: string }> {
    const notificationData: CreateNotificationData = {
      userId,
      type: 'error',
      title: 'Payment Failed',
      message: 'Your subscription payment failed. Please update your payment method to continue using premium features.',
      actionUrl: '/upgrade',
      metadata: {
        failureDate: new Date().toISOString(),
        notificationType: 'payment_failure'
      }
    }

    return await this.createNotification(notificationData)
  }

  // Create subscription expiry warning
  async createSubscriptionExpiryWarning(userId: string, daysRemaining: number): Promise<{ success: boolean; error?: string }> {
    const notificationData: CreateNotificationData = {
      userId,
      type: 'warning',
      title: 'Subscription Expiring Soon',
      message: `Your subscription will expire in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Renew now to continue using premium features.`,
      actionUrl: '/upgrade',
      metadata: {
        daysRemaining,
        warningDate: new Date().toISOString(),
        notificationType: 'subscription_expiry'
      }
    }

    return await this.createNotification(notificationData)
  }
}

export const notificationService = new NotificationService() 